import React from 'react';
import { motion } from 'framer-motion';
import { SectionLabel, SectionHeading } from './ui/UI';
import { Check, ArrowRight } from 'lucide-react';
const plans = [{
  name: 'Snapshot',
  price: '$299',
  cadence: 'one-time',
  note: 'Or free for select early access teams',
  desc: 'Best for teams that want to understand their top paths now.',
  features: ['One attack-path report', 'Limited cloud / identity / SaaS / code connections', 'Top validated paths', 'Basic remediation guidance', 'Shareable executive summary', 'Technical appendix', 'Early access onboarding'],
  cta: 'Request Snapshot Access'
}, {
  name: 'Team',
  price: '$799',
  cadence: '/month',
  note: '$7,990 annual',
  desc: 'Recurring validation for startups, SaaS, and small security teams.',
  features: ['Recurring attack-path validation', 'Cloud / identity / SaaS / code / external coverage', 'Top risk paths', 'Remediation guidance', 'Basic fix verification', 'Report sharing', 'Email and Slack notifications', 'Monthly evidence summary'],
  cta: 'Join Team Plan Beta'
}, {
  name: 'Business',
  price: '$1,999',
  cadence: '/month',
  note: '$19,990 annual',
  desc: 'For companies under customer, compliance, or AI security pressure.',
  features: ['Everything in Team', 'AI App Validation mission', 'Fix verification history', 'Customer-ready evidence packs', 'Cyber-insurance readiness', 'SOC 2 / ISO readiness', 'Jira integration', 'Quarterly executive summary'],
  cta: 'Join Business Plan Beta',
  highlight: true
}, {
  name: 'Scale',
  price: '$4,999',
  cadence: '/month',
  note: '$49,990 annual',
  desc: 'For distributed teams, business units, and AI product teams.',
  features: ['Everything in Business', 'Optional internal runner', 'Multiple teams / business units', 'SSO / RBAC', 'Policy controls', 'Audit logs', 'Expanded AI app validation', 'API access'],
  cta: 'Talk to Us About Scale'
}, {
  name: 'MSSP',
  price: '$2,500',
  cadence: '/month + per-client',
  note: '$250/client/month',
  desc: 'For MSSPs, vCISOs, and service providers delivering recurring validation.',
  features: ['Multi-tenant dashboard', 'Client-level validation reports', 'White-label evidence packs', 'Client risk summaries', 'Monthly QBR reports', 'Technician workflows', 'AI app validation add-on', 'Bulk onboarding'],
  cta: 'Join MSSP Partner Beta'
}, {
  name: 'Enterprise',
  price: 'Custom',
  cadence: '',
  note: 'Governance, scale, control',
  desc: 'For larger organizations, regulated teams, and enterprise security programs.',
  features: ['Custom validation scope', 'Private or dedicated runners', 'Advanced RBAC', 'Custom policies', 'Advanced audit logging', 'Security review support', 'Enterprise procurement support', 'Dedicated success'],
  cta: 'Contact Enterprise Access'
}];
const philosophy = ['Transparent', 'Expandable', 'Self-serve first', 'Report-driven', 'Evidence-based', 'Friendly to teams and MSSPs', 'Capable of scaling upmarket'];
export function Pricing() {
  return <section id="pricing" className="relative w-full py-32 border-t border-white/5" data-id="element-670">
      <div className="max-w-7xl mx-auto px-6 lg:px-8" data-id="element-671">
        <SectionLabel number="17" label="Pricing" className="mb-8" data-id="element-672" />

        <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 mb-16" data-id="element-673">
          <div className="lg:col-span-8" data-id="element-674">
            <SectionHeading size="xl" data-id="element-675">
              Enterprise outcome.
              <br data-id="element-676" />
              <span className="text-white/40" data-id="element-677">SaaS packaging.</span>
            </SectionHeading>
          </div>
          <div className="lg:col-span-4 lg:pt-4" data-id="element-678">
            <p className="text-lg text-white/60 leading-relaxed" data-id="element-679">
              Periscan pricing is designed to be transparent and usage-aware,
              scaling from first report to continuous validation. Final pricing
              may evolve during beta.
            </p>
          </div>
        </div>

        {/* Plans grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3 mb-20" data-id="element-680">
          {plans.map((p, i) => <motion.div key={i} initial={{
          opacity: 0,
          y: 20
        }} whileInView={{
          opacity: 1,
          y: 0
        }} viewport={{
          once: true
        }} transition={{
          duration: 0.4,
          delay: i * 0.05
        }} className={`relative p-8 rounded-2xl border transition-all ${p.highlight ? 'bg-gradient-to-br from-[#1a8fd4]/10 to-[#0a0d12] border-[#1a8fd4]/40' : 'bg-[#070a0e] border-white/[0.06] hover:border-white/10'}`} data-id="element-681">
              {p.highlight && <div className="absolute -top-3 left-8 px-3 py-1 bg-[#1a8fd4] text-white text-[10px] font-mono uppercase tracking-widest rounded-full" data-id="element-682">
                  Most Popular
                </div>}
              <div className="mb-6" data-id="element-683">
                <h3 className="text-2xl font-semibold text-white mb-2 tracking-tight" data-id="element-684">
                  {p.name}
                </h3>
                <p className="text-sm text-white/50 leading-relaxed min-h-[40px]" data-id="element-685">
                  {p.desc}
                </p>
              </div>

              <div className="mb-6 pb-6 border-b border-white/5" data-id="element-686">
                <div className="flex items-baseline gap-1" data-id="element-687">
                  <span className="text-4xl font-semibold text-white tracking-tight" data-id="element-688">
                    {p.price}
                  </span>
                  {p.cadence && <span className="text-sm text-white/40" data-id="element-689">{p.cadence}</span>}
                </div>
                {p.note && <p className="font-mono text-xs text-[#1a8fd4]/80 mt-2" data-id="element-690">
                    {p.note}
                  </p>}
              </div>

              <ul className="space-y-3 mb-8" data-id="element-691">
                {p.features.map((f, j) => <li key={j} className="flex items-start gap-3" data-id="element-692">
                    <Check className="w-4 h-4 text-[#1a8fd4] flex-shrink-0 mt-0.5" data-id="element-693" />
                    <span className="text-sm text-white/70" data-id="element-694">{f}</span>
                  </li>)}
              </ul>

              <a href="#early-access" className={`group w-full flex items-center justify-between gap-2 px-5 py-3 rounded-full text-sm font-medium transition-colors ${p.highlight ? 'bg-white text-black hover:bg-white/90' : 'bg-white/5 hover:bg-white/10 text-white border border-white/10'}`} data-id="element-695">
                <span data-id="element-696">{p.cta}</span>
                <ArrowRight className="w-4 h-4 transition-transform group-hover:translate-x-0.5" data-id="element-697" />
              </a>
            </motion.div>)}
        </div>

        {/* Pricing philosophy */}
        <div className="rounded-2xl bg-gradient-to-br from-[#0a0d12] to-[#070a0e] border border-white/5 p-8 md:p-12" data-id="element-698">
          <p className="font-mono text-xs uppercase tracking-widest text-[#1a8fd4] mb-4" data-id="element-699">
            Pricing Philosophy
          </p>
          <p className="text-xl md:text-2xl text-white leading-relaxed mb-8 max-w-3xl tracking-tight" data-id="element-700">
            Continuous validation should not require a six-figure platform, a
            long procurement cycle, or a dedicated red team.
          </p>
          <div className="flex flex-wrap gap-2" data-id="element-701">
            {philosophy.map((item, i) => <span key={i} className="px-4 py-2 text-sm text-white/80 bg-white/[0.03] border border-white/5 rounded-full" data-id="element-702">
                {item}
              </span>)}
          </div>
        </div>
      </div>
    </section>;
}